import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [detail, setDetail] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setDetail(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts < 20) setTimeout(poll, 2000);
        else setStatus("timeout");
      } catch {
        if (attempts < 10) setTimeout(poll, 2000);
        else setStatus("error");
      }
    };
    poll();
  }, [params]);

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4">
      <div className="bp-card rounded-2xl p-10 max-w-md w-full text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-10 h-10 text-[#D4AF37] mx-auto animate-spin" />
            <h2 className="font-heading text-2xl text-white mt-4">Processing payment...</h2>
            <p className="text-gray-400 text-sm mt-2">Verifying ownership on ledger.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-[#D4AF37] mx-auto" />
            <h2 className="font-heading text-3xl text-white mt-4" data-testid="checkout-success-title">
              Payment successful
            </h2>
            <p className="text-gray-400 text-sm mt-2">
              Your bricks have been assigned. View your digital certificates in My Tokens.
            </p>
            <div className="flex gap-3 mt-6">
              <Button className="flex-1 btn-gold rounded-full" onClick={() => navigate("/my-tokens")} data-testid="checkout-view-tokens">
                View My Tokens
              </Button>
              <Button variant="outline" className="flex-1 rounded-full" onClick={() => navigate("/dashboard")}>
                Dashboard
              </Button>
            </div>
          </>
        )}
        {(status === "expired" || status === "timeout" || status === "error") && (
          <>
            <XCircle className="w-10 h-10 text-red-400 mx-auto" />
            <h2 className="font-heading text-2xl text-white mt-4">Payment could not be confirmed</h2>
            <p className="text-gray-400 text-sm mt-2">
              {detail?.payment_status || status}
            </p>
            <Button className="mt-6 rounded-full" variant="outline" onClick={() => navigate("/properties")}>
              Back to properties
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
